import { Chip } from "@connected-repo/ui-mui/data-display/Chip";
import { Typography } from "@connected-repo/ui-mui/data-display/Typography";
import { Card } from "@connected-repo/ui-mui/layout/Card";
import type { MinuteLedgerSelectAll } from "@connected-repo/zod-schemas/minute_ledger.zod";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { absoluteDateTime, formatDate, formatDateTime, formatMinutes } from "@superadmin/utils/format";

const KIND_LABEL: Record<MinuteLedgerSelectAll["kind"], { label: string; color: "success" | "warning" | "default" }> = {
	grant: { label: "Purchased", color: "success" },
	adjustment: { label: "Adjustment", color: "warning" },
	usage: { label: "Usage", color: "default" },
};

interface MinutesLedgerTableProps {
	entries: MinuteLedgerSelectAll[];
	showActor?: boolean;
}

export const MinutesLedgerTable = ({ entries, showActor = false }: MinutesLedgerTableProps) => (
	<Card sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider", overflowX: "auto" }}>
		<Table size="small">
			<TableHead>
				<TableRow>
					<TableCell>When</TableCell>
					<TableCell>Type</TableCell>
					<TableCell align="right">Minutes</TableCell>
					<TableCell>Reference</TableCell>
					<TableCell>Note</TableCell>
					<TableCell>Expires</TableCell>
					{showActor && <TableCell>By</TableCell>}
				</TableRow>
			</TableHead>
			<TableBody>
				{entries.length === 0 && (
					<TableRow>
						<TableCell colSpan={showActor ? 7 : 6}>
							<Typography color="text.secondary" sx={{ py: 2 }}>
								No minutes have been assigned yet.
							</Typography>
						</TableCell>
					</TableRow>
				)}
				{entries.map((e) => {
					const kind = KIND_LABEL[e.kind];
					return (
						<TableRow key={e.id}>
							<TableCell sx={{ whiteSpace: "nowrap" }} title={absoluteDateTime(e.createdAt)}>
								{formatDateTime(e.createdAt)}
							</TableCell>
							<TableCell>
								<Chip
									label={e.kind === "usage" && e.product === "room" ? `${kind.label} · room` : kind.label}
									size="small"
									color={kind.color}
									variant="outlined"
								/>
							</TableCell>
							<TableCell align="right">
								<Typography sx={{ fontWeight: 700, color: e.minutes >= 0 ? "success.main" : "error.main" }}>
									{e.minutes > 0 ? "+" : ""}
									{formatMinutes(e.minutes)}
								</Typography>
							</TableCell>
							<TableCell>{e.reference ?? "—"}</TableCell>
							<TableCell sx={{ maxWidth: 320 }}>{e.note ?? "—"}</TableCell>
							<TableCell title={absoluteDateTime(e.expiresAt)}>{formatDate(e.expiresAt)}</TableCell>
							{showActor && (
								<TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
									{e.createdByUserId ? e.createdByUserId.slice(0, 8) : "system"}
								</TableCell>
							)}
						</TableRow>
					);
				})}
			</TableBody>
		</Table>
	</Card>
);
